import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    q: 'Is this just a wrapper over Perplexity / ChatGPT?',
    a: 'No. Perplexity is one of three channels. The differentiation is the orchestrator (multi-channel, budget-capped, audit-trailed), the source-cited / confidence-scored output format, and the optional voice and email channels — none of which Perplexity offers.',
  },
  {
    q: 'How is voice calling legal?',
    a: 'Research calls to public business lines aren\'t covered by TCPA, DNC, or FTC TSR — those apply to sales/marketing. Every call discloses AI identity, recording, and purpose at the top, covering two-party-consent states (CA, FL, IL, MD, MA, MT, NV, NH, PA, WA). Same legal posture as journalists, mystery shoppers, and Avalara\'s human researchers.',
  },
  {
    q: 'What\'s the privacy story with external AI?',
    a: 'The One Research is the only product on The One Stack platform that intentionally calls external AI APIs (Perplexity Sonar Pro). Every external call is logged in your audit trail with the uses_external_ai flag. You can disable external AI per-job and fall back to spider-only research. BYOK available on Enterprise.',
  },
  {
    q: 'How is my research data isolated from other customers?',
    a: 'Your briefs, jobs, transcripts, and email threads live in your tenanted workspace and are not visible to any other customer. Audit trails, cost ledgers, and source citations are tied to your job IDs only.',
  },
  {
    q: 'Can MSPs resell this to their customers?',
    a: 'Yes. The One Stack platform supports MSP-resale of every product. You set your customer-facing markup; we charge you wholesale. White-label branding available on Enterprise.',
  },
  {
    q: 'How accurate is the data?',
    a: 'Every fact has a confidence score (0–1) and at least one source URL. Auto-apply threshold defaults to 0.8; below that, results land in a review queue. Customer-facing UI surfaces the source on every claim. Best-effort with a strong audit trail — no claim that we\'re a tax advisor, registered investigator, or compliance service.',
  },
  {
    q: 'What if THEO can\'t close the research?',
    a: 'Three escalation tiers: web-only fallback, voice escalation, and (future) human review queue staffed by remote workers. If the answer can\'t be found, you don\'t pay for the dead-end work — only for the actual API/voice costs incurred.',
  },
  {
    q: 'Pricing — what does usage actually cost?',
    a: 'A typical lead-discovery job in a midsize metro lands around $5–15 (often within your $5 starter credit). A vendor due-diligence one-pager: $3–7. A tax-rate refresh across all 52 US jurisdictions: under $2 total. Competitor monitoring: $1–3 per competitor per week. Per-action rates with markup are itemized in your dashboard before you confirm any job.',
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="relative bg-ink-950 py-24">
      <div className="mx-auto max-w-3xl px-6">
        <div className="mb-12 text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-rose-400">
            FAQ
          </p>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Questions, answered.
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-xl border border-white/10 bg-ink-900/50"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition hover:bg-white/5"
              >
                <span className="font-semibold text-white">{f.q}</span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-rose-400 transition-transform ${
                    open === i ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-sm leading-relaxed text-slate-400">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
